import {
  getBinaryOutcomeValues,
  isMarketResolved,
  parseMarketOutcomes,
} from '@/utils/market-outcomes';
import { filterListedMarkets } from '@/utils/market-list-status';

function normalizeOutcomeLabels(outcomesField) {
  if (Array.isArray(outcomesField)) {
    return outcomesField.map((s) => String(s ?? '').trim()).filter(Boolean);
  }
  return parseMarketOutcomes(outcomesField);
}

/** outcomes 数组长度为 2 即为二元市场（含 Up/Down、Yes/No 等） */
export function isBinaryOutcomes(outcomesField) {
  return normalizeOutcomeLabels(outcomesField).length === 2;
}

/** outcomes 是否为标准 Yes/No 二元市场 */
export function isYesNoOutcomes(outcomesField) {
  const labels = normalizeOutcomeLabels(outcomesField).map((s) => s.toLowerCase());
  return labels.length === 2 && labels[0] === 'yes' && labels[1] === 'no';
}

/** outcomes 是否为 Over/Under 二元市场（大小球等） */
export function isOverUnderOutcomes(outcomesField) {
  const labels = normalizeOutcomeLabels(outcomesField).map((s) => s.toLowerCase());
  return labels.length === 2 && labels[0] === 'over' && labels[1] === 'under';
}

/** 是否使用 explore.* 标准二元 outcome 文案（Yes/No、Over/Under） */
export function usesStandardBinaryOutcomeI18n(outcomesField) {
  return isYesNoOutcomes(outcomesField) || isOverUnderOutcomes(outcomesField);
}

/**
 * 双队名主盘：两个 outcome，且不是 Yes/No、Over/Under、Up/Down。
 * 用 outcome 形态判断，不解析 title。
 */
export function isNamedTwoWayOutcomes(outcomesField) {
  if (!isBinaryOutcomes(outcomesField)) return false;
  if (usesStandardBinaryOutcomeI18n(outcomesField)) return false;
  const labels = normalizeOutcomeLabels(outcomesField).map((s) => s.toLowerCase());
  return !(labels[0] === 'up' && labels[1] === 'down');
}

/** listed 池中第一盘未结算的双队名主盘 */
export function findNamedMatchupMarket(markets) {
  const list = Array.isArray(markets) ? markets : [];
  return list.find((m) => isNamedTwoWayOutcomes(m?.outcomes) && !isMarketResolved(m)) || null;
}

/** 列表/详情/卡片展示：标准二元走 i18n，其余用 API 原值 */
export function getBinaryOutcomeDisplayLabels(outcomesField, t) {
  if (isYesNoOutcomes(outcomesField)) {
    return { yesLabel: t('explore.yes'), noLabel: t('explore.no') };
  }
  if (isOverUnderOutcomes(outcomesField)) {
    return { yesLabel: t('explore.over'), noLabel: t('explore.under') };
  }
  const { yes, no } = getBinaryOutcomeValues(normalizeOutcomeLabels(outcomesField));
  return { yesLabel: yes, noLabel: no };
}

/** 交易弹窗 binary 按钮文案（与卡片同一套 explore.* key） */
export function getBinaryTradeLabelOpts(outcomesField, t) {
  if (!usesStandardBinaryOutcomeI18n(outcomesField)) return {};
  const { yesLabel, noLabel } = getBinaryOutcomeDisplayLabels(outcomesField, t);
  return { yesLabel, noLabel };
}

const ENDED_MARKET_STATUSES = new Set([
  'resolved',
  'cancelled',
  'tradinghalted',
  'awaitingresult',
  'resultproposed',
  'challenged',
  'disputed',
  'paused',
]);

/**
 * collection/get · market 生命周期：pending | active | ended
 * 与详情页状态徽章、complete variant 共用。
 * 有上架子盘时：任一未结算 → active；全部结算 → ended（忽略过期 endDate）。
 */
export function resolveMarketLifecycleStatus({
  closed,
  active,
  marketStatus,
  endDate,
  markets,
} = {}) {
  const status = String(marketStatus ?? '')
    .trim()
    .toLowerCase();
  if (status === 'pending') return 'pending';

  const listed = filterListedMarkets(markets);
  if (listed.length > 0) {
    // 只看子盘自身 closed / marketStatus，不回落到 collection
    return listed.some((m) => !isMarketResolved(m)) ? 'active' : 'ended';
  }

  if (Number(closed) === 1) return 'ended';
  if (ENDED_MARKET_STATUSES.has(status)) return 'ended';

  const endMs = Date.parse(endDate ?? '');
  if (Number.isFinite(endMs) && Date.now() > endMs) return 'ended';

  if (Number(active) === 0) return 'ended';

  return 'active';
}

/**
 * 解析市场卡片/详情 variant：complete | multi | binary
 * - 多个二元子 market → binary（行列表 + Yes/No）
 * - 单 market · outcomes > 2 → multi（outcome 按钮）
 * - 单 market · 二元 outcomes → binary
 */
export function resolveMarketVariant({
  questionType,
  closed,
  active,
  outcomes,
  markets,
  marketStatus,
  endDate,
} = {}) {
  if (
    resolveMarketLifecycleStatus({ closed, active, marketStatus, endDate, markets }) === 'ended'
  ) {
    return 'complete';
  }

  const marketList = Array.isArray(markets) ? markets : [];
  const binaryMarketCount = marketList.filter((m) => isBinaryOutcomes(m?.outcomes)).length;
  if (binaryMarketCount > 1) return 'binary';

  const firstOutcomes = outcomes ?? marketList[0]?.outcomes;
  const outcomeCount = normalizeOutcomeLabels(firstOutcomes).length;
  if (outcomeCount > 2) return 'multi';

  const type = String(questionType || '')
    .trim()
    .toLowerCase();
  if (type === 'multi' || type === 'numerical' || type === 'date') return 'multi';
  if (type === 'binary') return 'binary';

  return isBinaryOutcomes(firstOutcomes) ? 'binary' : 'multi';
}
